/**
 * Project-level commercial terms helpers.
 *
 * User-facing percentages are entered as decimal percentages (for example
 * "18" or "17.5"). API/DB storage uses basis points so values are exact:
 * 100.00% = 10000 bps, 18.00% = 1800 bps.
 */

/** Parse a percentage input into basis points. Returns null for empty/invalid. */
export function parsePercentInputToBps(input: string): number | null {
  if (input == null) return null;
  const cleaned = input.replace(/%/g, '').trim();
  if (cleaned === '') return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(cleaned)) return null;
  const percent = Number(cleaned);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null;
  return Math.round(percent * 100);
}

/** Format basis points as a percentage with two decimals. */
export function formatBpsAsPercent(bps: number | null | undefined): string {
  if (bps == null) return '—';
  return `${(bps / 100).toFixed(2)}%`;
}

/** Compute GST amount in paisa from store value and GST rate. */
export function deriveGstAmountPaisa(
  totalStoreValuePaisa: number | null | undefined,
  gstRateBps: number | null | undefined,
): number | null {
  if (totalStoreValuePaisa == null || gstRateBps == null) return null;
  return Math.round((totalStoreValuePaisa * gstRateBps) / 10_000);
}

/** Compute declared total contract value = store value + GST amount. */
export function deriveDeclaredTotalContractValuePaisa(
  totalStoreValuePaisa: number | null | undefined,
  gstRateBps: number | null | undefined,
): number | null {
  const gstAmount = deriveGstAmountPaisa(totalStoreValuePaisa, gstRateBps);
  if (totalStoreValuePaisa == null || gstAmount == null) return null;
  return totalStoreValuePaisa + gstAmount;
}

/** Payment terms are optional, but if one side is set, both must sum to 100%. */
export function isValidPaymentSplitBps(
  paymentOnINoteBps: number | null | undefined,
  paymentOnCrvBps: number | null | undefined,
): boolean {
  const iNoteSet = paymentOnINoteBps != null;
  const crvSet = paymentOnCrvBps != null;
  if (!iNoteSet && !crvSet) return true;
  if (!iNoteSet || !crvSet) return false;
  return paymentOnINoteBps + paymentOnCrvBps === 10_000;
}
